import * as signalR from '@microsoft/signalr';
import { printReceipt as sendPrintReceipt } from './localPrintService';

const PAVO_API_URL = process.env.REACT_APP_GTS_PAVO_API_URL || 'http://localhost:5011';
const LOCAL_SERVICE_URL_DEFAULT = 'http://localhost:3003';

/**
 * F5: Pavo cihazindan gelen yazdirma istegi (SignalR fallback).
 * `OnLocalPrintRequest` event'inde gelir; icinde local-service'e iletilecek
 * orijinal payload vardir.
 */
export interface LocalPrintRequestEnvelope {
  requestId: string;
  type?: string;
  /** local-service'e aynen POST edilecek body (`{ printerId, printerName, receiptData }`) */
  payload: any;
  /** Opsiyonel override. Yoksa web client'in bildigi localServiceUrl kullanilir. */
  localServiceUrl?: string;
}

class PavoSignalRService {
  private connection: signalR.HubConnection | null = null;
  private companyId: string | null = null;
  private branchId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  private paymentCompletedCallbacks: Map<string, (requestId: string, result: any) => void> = new Map();
  private paymentFailedCallbacks: Map<string, (requestId: string, errorCode: string, message: string) => void> = new Map();
  private paymentCancelledCallbacks: Map<string, (requestId: string) => void> = new Map();
  private deviceStatusCallbacks: ((deviceCode: string, isOnline: boolean) => void)[] = [];
  private statusChangeCallbacks: ((status: signalR.HubConnectionState) => void)[] = [];

  // ✅ Rest-P2P (pavo_rest_p2p) ödeme event callback'leri
  private quickSalePaymentCompletedCallbacks: Map<string, (requestId: string, result: any) => void> = new Map();
  private quickSalePaymentFailedCallbacks: Map<string, (requestId: string, errorCode: string, message: string) => void> = new Map();
  private quickSalePaymentCancelledCallbacks: Map<string, (requestId: string, reason?: string) => void> = new Map();
  private quickSalePaymentProgressCallbacks: Map<string, (requestId: string, progress: any) => void> = new Map();

  // ✅ F4: Rest-P2P iade/iptal/sorgu event callback'leri
  private pavoRefundCompletedCallbacks: Map<string, (requestId: string, action: string, result: any) => void> = new Map();
  private pavoRefundFailedCallbacks: Map<string, (requestId: string, action: string, errorCode: string, message: string) => void> = new Map();

  // ✅ Phase 1: Rest-P2P TURMOB musteri sorgu event callback'leri
  private pavoCustomerQueryCompletedCallbacks: Map<string, (requestId: string, result: any) => void> = new Map();
  private pavoCustomerQueryFailedCallbacks: Map<string, (requestId: string, errorCode: string, message: string) => void> = new Map();

  // ✨ Pavo Restoran Ödendi Onay event callback'leri
  // Pavo cihazı restoran masasında ödeme alıp ActiveSale'i PavoPaymentPending=true
  // yaptığında yayılan event'ler. Web bu event'lere abone olarak masa listesini
  // anlık (10 sn polling beklemeden) tazeleyebilir.
  private tablePavoClosedCallbacks: Map<string, (payload: any) => void> = new Map();

  // ✅ Pavo cihaz restoran modu masa event'leri (RestaurantDeviceController broadcast'leri)
  // Cihaz masaya ürün eklediğinde/masayı kapattığında web masa listesini anında tazelesin.
  private tableUpdatedCallbacks: Map<string, (payload: any) => void> = new Map();
  private tableClosedCallbacks: Map<string, (payload: any) => void> = new Map();

  // ───────────────────────────────────────────────
  // 🔄 REST-P2P Reconciliation — getSaleResult zinciri
  // Kiosk pariteli presence tracking + OnQuickSaleResultResolved.
  // ───────────────────────────────────────────────

  /** Real-time online Pavo cihazlari (DeviceConnected/DeviceDisconnected + GetOnlineDevices snapshot). */
  private _onlineDevices: Set<string> = new Set();

  /** UI abonelikleri: deviceCode -> (subId -> cb). */
  private _devicePresenceSubs: Map<
    string,
    Map<string, (deviceCode: string, isOnline: boolean) => void>
  > = new Map();

  /** OnQuickSaleResultResolved abonelikleri (subId -> cb). */
  private quickSaleResultResolvedCallbacks: Map<
    string,
    (requestId: string, result: any) => void
  > = new Map();

  /** F5: Local print forwarding icin kullanilacak local-service URL. */
  private localServiceUrl: string = LOCAL_SERVICE_URL_DEFAULT;

  /** F5: Local print event'lerinde UI'a haber vermek icin opsiyonel callback. */
  private localPrintHandlers: Map<
    string,
    (env: LocalPrintRequestEnvelope, result: { success: boolean; message?: string }) => void
  > = new Map();

  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }

  async connect(companyId: string, branchId?: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('[PavoSignalR] Zaten bagli (listener sayaci kontrol ediliyor)');
      // HMR / hot-reload sonrasi listener kaybolmus olabilir — guvenlik icin yeniden register et
      this.reRegisterListenersIfNeeded();
      return;
    }

    this.companyId = companyId;
    this.branchId = branchId || null;

    if (this.connection) {
      try { await this.connection.stop(); } catch (_) {}
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PAVO_API_URL}/hubs/pavo`, {
        withCredentials: false,
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => ctx.previousRetryCount < 3 ? 3000 : 10000,
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onclose(() => {
      console.log('[PavoSignalR] Baglanti kapandi');
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      // Presence: hub kopuştu — tüm cihazları offline işaretle (UI anında reconciliation tetikleyebilsin).
      this._markAllDevicesOffline();
      this.attemptReconnect();
    });

    this.connection.onreconnecting(() => {
      console.log('[PavoSignalR] Yeniden baglaniyor...');
      this.notifyStatusChange(signalR.HubConnectionState.Reconnecting);
    });

    this.connection.onreconnected(() => {
      console.log('[PavoSignalR] Yeniden baglandi');
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      this.reconnectAttempts = 0;
      this.joinGroups();
      // Snapshot tazele — reconnect sirasinda DeviceConnected/Disconnected event'leri kacirilmis olabilir.
      this._refreshDevicePresenceSnapshot();
    });

    this.registerListeners();

    try {
      await this.connection.start();
      console.log('[PavoSignalR] Baglanti kuruldu');
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      this.reconnectAttempts = 0;
      await this.joinGroups();
      // İlk bağlantı: online cihaz snapshot'ı al.
      await this._refreshDevicePresenceSnapshot();
    } catch (error) {
      console.error('[PavoSignalR] Baglanti hatasi:', error);
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      this.attemptReconnect();
    }
  }

  /**
   * Hot-reload / reconnect sonrasi listener'lari yeniden baglamak icin guvenlik metodu.
   * `connection.on(name, ...)` aynisini iki kez eklerse SignalR client'i iki kez tetikler;
   * bu yuzden once `connection.off(name)` yapip yeniden register ediyoruz.
   */
  private reRegisterListenersIfNeeded(): void {
    if (!this.connection) return;
    const eventNames = [
      'OnPaymentCompleted',
      'OnPaymentFailed',
      'OnPaymentCancelled',
      'DeviceConnected',
      'DeviceDisconnected',
      'OnLocalPrintRequest',
      'OnQuickSalePaymentCompleted',
      'OnQuickSalePaymentFailed',
      'OnQuickSalePaymentCancelled',
      'OnQuickSalePaymentProgress',
      'OnPavoRefundCompleted',
      'OnPavoRefundFailed',
      'OnPavoCustomerQueryCompleted',
      'OnPavoCustomerQueryFailed',
      'OnTablePavoClosed',
      'OnTableUpdated',
      'OnTableClosed',
      'OnQuickSaleResultResolved',
      'DeviceHeartbeatReceived', // 🔇 sessiz no-op (eski backend uyarı susturma)
    ];
    for (const n of eventNames) {
      try { this.connection.off(n); } catch (_) {}
    }
    this.registerListeners();
    console.log('[PavoSignalR] 🔄 Listener\'lar yeniden baglandi (HMR / reconnect guvenligi)');
  }

  private registerListeners(): void {
    if (!this.connection) return;

    this.connection.on('OnPaymentCompleted', (requestId: string, result: any) => {
      console.log('[PavoSignalR] Odeme tamamlandi:', requestId);
      this.paymentCompletedCallbacks.forEach((cb) => cb(requestId, result));
    });

    this.connection.on('OnPaymentFailed', (requestId: string, errorCode: string, message: string) => {
      console.log('[PavoSignalR] Odeme basarisiz:', requestId, errorCode);
      this.paymentFailedCallbacks.forEach((cb) => cb(requestId, errorCode, message));
    });

    this.connection.on('OnPaymentCancelled', (requestId: string) => {
      console.log('[PavoSignalR] Odeme iptal edildi:', requestId);
      this.paymentCancelledCallbacks.forEach((cb) => cb(requestId));
    });

    this.connection.on('DeviceConnected', (deviceCode: string) => {
      // Real-time presence: anlık set'i güncelle, abonelikleri tetikle.
      const wasOnline = this._onlineDevices.has(deviceCode);
      this._onlineDevices.add(deviceCode);
      this.deviceStatusCallbacks.forEach((cb) => cb(deviceCode, true));
      if (!wasOnline) this._notifyDevicePresence(deviceCode, true);
    });

    this.connection.on('DeviceDisconnected', (deviceCode: string) => {
      const wasOnline = this._onlineDevices.has(deviceCode);
      this._onlineDevices.delete(deviceCode);
      this.deviceStatusCallbacks.forEach((cb) => cb(deviceCode, false));
      if (wasOnline) this._notifyDevicePresence(deviceCode, false);
    });

    // 🔄 REST-P2P Reconciliation cevabı (Pavo getSaleResult -> backend -> caller)
    this.connection.on('OnQuickSaleResultResolved', (requestId: string, result: any) => {
      console.log('[PavoSignalR] 🔄 Rest-P2P reconciliation resolved:', requestId, result);
      this.quickSaleResultResolvedCallbacks.forEach((cb) => cb(requestId, result));
    });

    // F5: Pavo cihazi ic aga ulasamadiginda SignalR ile bize "yazdir" der.
    // Biz local-service'e HTTP ile forward edip sonucu `AckLocalPrint` ile
    // geri bildiririz.
    this.connection.on('OnLocalPrintRequest', async (envelope: LocalPrintRequestEnvelope) => {
      console.log('[PavoSignalR] 🖨️ OnLocalPrintRequest geldi:', envelope?.requestId);
      await this.handleLocalPrintRequest(envelope);
    });

    // ✅ Rest-P2P event'leri (Pavo -> Web)
    this.connection.on('OnQuickSalePaymentCompleted', (requestId: string, result: any) => {
      console.log('[PavoSignalR] ✅ Rest-P2P Odeme tamamlandi:', requestId, result);
      this.quickSalePaymentCompletedCallbacks.forEach((cb) => cb(requestId, result));
    });

    this.connection.on('OnQuickSalePaymentFailed', (requestId: string, errorCode: string, message: string) => {
      console.log('[PavoSignalR] ❌ Rest-P2P Odeme basarisiz:', requestId, errorCode, message);
      this.quickSalePaymentFailedCallbacks.forEach((cb) => cb(requestId, errorCode, message));
    });

    // Backend yeni hub method `(requestId, reason)` 2 argument gonderir; eski
    // (requestId) ile geriye donuk uyumlu kalmak icin reason opsiyonel.
    this.connection.on('OnQuickSalePaymentCancelled', (requestId: string, reason?: string) => {
      console.log('[PavoSignalR] 🚫 Rest-P2P Odeme iptal edildi:', requestId, reason);
      this.quickSalePaymentCancelledCallbacks.forEach((cb) => cb(requestId, reason));
    });

    this.connection.on('OnQuickSalePaymentProgress', (requestId: string, progress: any) => {
      console.log('[PavoSignalR] ⏳ Rest-P2P ilerleme:', requestId, progress);
      this.quickSalePaymentProgressCallbacks.forEach((cb) => cb(requestId, progress));
    });

    // ✅ F4: Rest-P2P iade/iptal/sorgu event'leri (Pavo -> Web)
    this.connection.on('OnPavoRefundCompleted', (requestId: string, action: string, result: any) => {
      console.log('[PavoSignalR] ✅ Rest-P2P Refund tamamlandi:', requestId, action, result);
      this.pavoRefundCompletedCallbacks.forEach((cb) => cb(requestId, action, result));
    });

    this.connection.on('OnPavoRefundFailed', (requestId: string, action: string, errorCode: string, message: string) => {
      console.log('[PavoSignalR] ❌ Rest-P2P Refund basarisiz:', requestId, action, errorCode, message);
      this.pavoRefundFailedCallbacks.forEach((cb) => cb(requestId, action, errorCode, message));
    });

    // ✅ Phase 1: Rest-P2P TURMOB musteri sorgusu sonuclari (Pavo -> Web)
    this.connection.on('OnPavoCustomerQueryCompleted', (requestId: string, result: any) => {
      console.log('[PavoSignalR] ✅ Rest-P2P CustomerQuery tamamlandi:', requestId, result);
      this.pavoCustomerQueryCompletedCallbacks.forEach((cb) => cb(requestId, result));
    });

    this.connection.on('OnPavoCustomerQueryFailed', (requestId: string, errorCode: string, message: string) => {
      console.log('[PavoSignalR] ❌ Rest-P2P CustomerQuery basarisiz:', requestId, errorCode, message);
      this.pavoCustomerQueryFailedCallbacks.forEach((cb) => cb(requestId, errorCode, message));
    });

    // ✨ Pavo Restoran Ödendi Onay — Pavo'dan ödeme alındı, web masayı KOYU MOR'a alsın
    this.connection.on('OnTablePavoClosed', (payload: any) => {
      console.log('[PavoSignalR] 🟣 Pavo restoran masası ödendi (pending):', payload);
      this.tablePavoClosedCallbacks.forEach((cb) => cb(payload));
    });

    // ✅ Pavo cihaz restoran modu — masa güncellendi (ürün eklendi/taşındı vb.)
    this.connection.on('OnTableUpdated', (payload: any) => {
      this.tableUpdatedCallbacks.forEach((cb) => cb(payload));
    });

    // ✅ Pavo cihaz restoran modu — masa kapatıldı
    this.connection.on('OnTableClosed', (payload: any) => {
      this.tableClosedCallbacks.forEach((cb) => cb(payload));
    });

    // 🔇 DeviceHeartbeatReceived — Backend `Clients.All.SendAsync("DeviceHeartbeatReceived", ...)`
    // çağrısı (PavoHub.cs:DeviceHeartbeat) artık kaldırıldı. Yine de eski backend'le
    // (henüz deploy edilmemiş) bağlanan client'larda "No client method ..." uyarısı
    // çıkmasın diye sessiz bir no-op handler tutuyoruz. Bu handler hiçbir şey yapmaz;
    // sadece SignalR client warning'ini susturur. Yeni heartbeat event'leri eklenirse
    // burada da listelenebilir.
    this.connection.on('DeviceHeartbeatReceived', () => {
      // intentional no-op (presence için DeviceConnected/Disconnected kullanılıyor)
    });
  }

  private async handleLocalPrintRequest(envelope: LocalPrintRequestEnvelope): Promise<void> {
    if (!envelope || !envelope.requestId || !envelope.payload) {
      console.warn('[PavoSignalR] OnLocalPrintRequest eksik envelope, atlandi');
      return;
    }
    const baseUrl = envelope.localServiceUrl || this.localServiceUrl || LOCAL_SERVICE_URL_DEFAULT;
    let success = false;
    let message: string | undefined;
    try {
      await sendPrintReceipt(envelope.payload as any, { baseUrl, timeoutMs: 8000 });
      success = true;
    } catch (err: any) {
      success = false;
      message = err?.message || 'Local-service yanit vermedi';
      console.error('[PavoSignalR] ❌ Local-service forward basarisiz:', err);
    }

    // UI'a bilgi ver (ornegin bir toast icin)
    const result = { success, message };
    this.localPrintHandlers.forEach((cb) => {
      try {
        cb(envelope, result);
      } catch (e) {
        console.error('[PavoSignalR] local print handler hatasi:', e);
      }
    });

    // ACK'i hub'a gonder -> Pavo cihazi LocalPrintAck olarak alir.
    if (!this.companyId || !this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[PavoSignalR] ACK gonderilemedi: hub/companyId yok');
      return;
    }
    try {
      await this.connection.invoke('AckLocalPrint', this.companyId, {
        requestId: envelope.requestId,
        success,
        message,
        handledBy: 'gts-web',
      });
    } catch (e) {
      console.error('[PavoSignalR] AckLocalPrint hatasi:', e);
    }
  }

  /** F5: Local-service URL override (ornegin ayarlar sayfasindan). */
  setLocalServiceUrl(url: string | undefined | null): void {
    if (url && typeof url === 'string' && url.trim() !== '') {
      this.localServiceUrl = url.trim();
    }
  }

  /** F5: Yazdirma istegi/ack'i izlemek icin UI abonesi ekle. */
  onLocalPrintRequest(
    id: string,
    handler: (env: LocalPrintRequestEnvelope, result: { success: boolean; message?: string }) => void
  ): void {
    this.localPrintHandlers.set(id, handler);
  }

  offLocalPrintRequest(id: string): void {
    this.localPrintHandlers.delete(id);
  }

  private async joinGroups(): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;
    try {
      if (this.companyId) await this.connection.invoke('JoinCompanyGroup', this.companyId);
      if (this.branchId) await this.connection.invoke('JoinBranchGroup', this.branchId);
    } catch (error) {
      console.error('[PavoSignalR] Gruplara katilma hatasi:', error);
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    setTimeout(() => {
      if (this.companyId) this.connect(this.companyId, this.branchId || undefined);
    }, 5000);
  }

  async sendPaymentRequest(deviceCode: string, paymentData: any): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('PavoSignalR baglantisi yok');
    }
    await this.connection.invoke('SendPaymentRequest', deviceCode, paymentData);
  }

  async cancelPaymentRequest(deviceCode: string, requestId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;
    await this.connection.invoke('CancelPaymentRequest', deviceCode, requestId);
  }

  onPaymentCompleted(id: string, callback: (requestId: string, result: any) => void): void {
    this.paymentCompletedCallbacks.set(id, callback);
  }

  onPaymentFailed(id: string, callback: (requestId: string, errorCode: string, message: string) => void): void {
    this.paymentFailedCallbacks.set(id, callback);
  }

  onPaymentCancelled(id: string, callback: (requestId: string) => void): void {
    this.paymentCancelledCallbacks.set(id, callback);
  }

  onDeviceStatus(callback: (deviceCode: string, isOnline: boolean) => void): void {
    this.deviceStatusCallbacks.push(callback);
  }

  onStatusChange(callback: (status: signalR.HubConnectionState) => void): void {
    this.statusChangeCallbacks.push(callback);
    if (this.connection) callback(this.connection.state);
  }

  private notifyStatusChange(status: signalR.HubConnectionState): void {
    this.statusChangeCallbacks.forEach((cb) => cb(status));
  }

  removeCallbacks(id: string): void {
    this.paymentCompletedCallbacks.delete(id);
    this.paymentFailedCallbacks.delete(id);
    this.paymentCancelledCallbacks.delete(id);
  }

  // ───────────────────────────────────────────────
  // ✅ Rest-P2P (pavo_rest_p2p) callback abonelikleri
  // ───────────────────────────────────────────────
  onQuickSalePaymentCompleted(id: string, callback: (requestId: string, result: any) => void): void {
    this.quickSalePaymentCompletedCallbacks.set(id, callback);
  }

  onQuickSalePaymentFailed(id: string, callback: (requestId: string, errorCode: string, message: string) => void): void {
    this.quickSalePaymentFailedCallbacks.set(id, callback);
  }

  onQuickSalePaymentCancelled(id: string, callback: (requestId: string, reason?: string) => void): void {
    this.quickSalePaymentCancelledCallbacks.set(id, callback);
  }

  onQuickSalePaymentProgress(id: string, callback: (requestId: string, progress: any) => void): void {
    this.quickSalePaymentProgressCallbacks.set(id, callback);
  }

  removeQuickSaleCallbacks(id: string): void {
    this.quickSalePaymentCompletedCallbacks.delete(id);
    this.quickSalePaymentFailedCallbacks.delete(id);
    this.quickSalePaymentCancelledCallbacks.delete(id);
    this.quickSalePaymentProgressCallbacks.delete(id);
  }

  // ───────────────────────────────────────────────
  // ✅ F4: Rest-P2P iade/iptal/sorgu callback abonelikleri
  // ───────────────────────────────────────────────
  onPavoRefundCompleted(id: string, callback: (requestId: string, action: string, result: any) => void): void {
    this.pavoRefundCompletedCallbacks.set(id, callback);
  }

  onPavoRefundFailed(id: string, callback: (requestId: string, action: string, errorCode: string, message: string) => void): void {
    this.pavoRefundFailedCallbacks.set(id, callback);
  }

  removePavoRefundCallbacks(id: string): void {
    this.pavoRefundCompletedCallbacks.delete(id);
    this.pavoRefundFailedCallbacks.delete(id);
  }

  // ───────────────────────────────────────────────
  // ✅ Phase 1: Rest-P2P TURMOB musteri sorgu callback abonelikleri
  // ───────────────────────────────────────────────
  onPavoCustomerQueryCompleted(id: string, callback: (requestId: string, result: any) => void): void {
    this.pavoCustomerQueryCompletedCallbacks.set(id, callback);
  }

  onPavoCustomerQueryFailed(id: string, callback: (requestId: string, errorCode: string, message: string) => void): void {
    this.pavoCustomerQueryFailedCallbacks.set(id, callback);
  }

  removePavoCustomerQueryCallbacks(id: string): void {
    this.pavoCustomerQueryCompletedCallbacks.delete(id);
    this.pavoCustomerQueryFailedCallbacks.delete(id);
  }

  // ───────────────────────────────────────────────
  // ✨ Pavo Restoran Ödendi Onay event abonelikleri
  // ───────────────────────────────────────────────
  /** Pavo cihazından restoran masası ödendi (PavoPaymentPending=true) */
  onTablePavoClosed(id: string, callback: (payload: any) => void): void {
    this.tablePavoClosedCallbacks.set(id, callback);
  }

  removeTablePavoClosedCallback(id: string): void {
    this.tablePavoClosedCallbacks.delete(id);
  }

  /** Pavo cihazı bir masayı güncelledi (ürün ekleme/taşıma vb.) */
  onTableUpdated(id: string, callback: (payload: any) => void): void {
    this.tableUpdatedCallbacks.set(id, callback);
  }

  removeTableUpdatedCallback(id: string): void {
    this.tableUpdatedCallbacks.delete(id);
  }

  /** Pavo cihazı bir masayı kapattı */
  onTableClosed(id: string, callback: (payload: any) => void): void {
    this.tableClosedCallbacks.set(id, callback);
  }

  removeTableClosedCallback(id: string): void {
    this.tableClosedCallbacks.delete(id);
  }

  /** Mevcut SignalR bağlantısının connectionId'sini döner (backend callerConnectionId için). */
  getConnectionId(): string | null {
    return this.connection?.connectionId || null;
  }

  // ───────────────────────────────────────────────
  // 🔄 REST-P2P Reconciliation — presence + resolve API'leri
  // Kiosk PavoSignalRService ile aynı pattern.
  // ───────────────────────────────────────────────

  /** Anlık olarak `deviceCode` online mı? (DeviceConnected/Disconnected + GetOnlineDevices snapshot'ı baz alır.) */
  isDeviceOnline(deviceCode: string): boolean {
    return this._onlineDevices.has(deviceCode);
  }

  /** Tüm online cihazların kopyası (read-only kullanım). */
  get onlineDevices(): Set<string> {
    return new Set(this._onlineDevices);
  }

  /**
   * Belirli bir `deviceCode` için presence değişikliklerini izle.
   * `subId` ile birden fazla bileşen aynı cihaza abone olabilir.
   * Callback `(deviceCode, isOnline)` aldığı her transition'da çağrılır.
   */
  addDevicePresenceSubscription(
    deviceCode: string,
    subId: string,
    cb: (deviceCode: string, isOnline: boolean) => void,
  ): void {
    let bucket = this._devicePresenceSubs.get(deviceCode);
    if (!bucket) {
      bucket = new Map();
      this._devicePresenceSubs.set(deviceCode, bucket);
    }
    bucket.set(subId, cb);
  }

  removeDevicePresenceSubscription(deviceCode: string, subId: string): void {
    const bucket = this._devicePresenceSubs.get(deviceCode);
    if (!bucket) return;
    bucket.delete(subId);
    if (bucket.size === 0) this._devicePresenceSubs.delete(deviceCode);
  }

  /** OnQuickSaleResultResolved aboneliği — reconciliation cevabını dinle. */
  onQuickSaleResultResolved(id: string, callback: (requestId: string, result: any) => void): void {
    this.quickSaleResultResolvedCallbacks.set(id, callback);
  }

  removeQuickSaleResultResolvedCallback(id: string): void {
    this.quickSaleResultResolvedCallbacks.delete(id);
  }

  private _notifyDevicePresence(deviceCode: string, isOnline: boolean): void {
    const bucket = this._devicePresenceSubs.get(deviceCode);
    if (!bucket) return;
    bucket.forEach((cb) => {
      try {
        cb(deviceCode, isOnline);
      } catch (e) {
        console.error('[PavoSignalR] device presence cb hata:', e);
      }
    });
  }

  /**
   * Hub'a `GetOnlineDevices` hub method'unu çağırıp set'i tazeler.
   * Reconnect veya ilk bağlantıda kaçırılmış DeviceConnected/Disconnected
   * event'lerini kurtarır; delta'ları abonelere yansıtır.
   */
  private async _refreshDevicePresenceSnapshot(): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;
    try {
      const list = (await this.connection.invoke<string[]>('GetOnlineDevices')) || [];
      const newSet = new Set(list);
      // Delta: yeni online (snapshot'ta var, eskide yoktu) -> notify true.
      newSet.forEach((dc) => {
        if (!this._onlineDevices.has(dc)) this._notifyDevicePresence(dc, true);
      });
      // Delta: artık offline (eskide vardı, snapshot'ta yok) -> notify false.
      this._onlineDevices.forEach((dc) => {
        if (!newSet.has(dc)) this._notifyDevicePresence(dc, false);
      });
      this._onlineDevices = newSet;
      console.log('[PavoSignalR] 🔄 device presence snapshot tazelendi:', list.length, 'cihaz online');
    } catch (e) {
      console.warn('[PavoSignalR] GetOnlineDevices hub call basarisiz (eski backend?):', e);
    }
  }

  private _markAllDevicesOffline(): void {
    // Hub kopuştu: tüm cihazları offline olarak işaretle ki UI reconciliation'a düşsün.
    const prev = Array.from(this._onlineDevices);
    this._onlineDevices.clear();
    prev.forEach((dc) => this._notifyDevicePresence(dc, false));
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try { await this.connection.stop(); } catch (_) {}
      this.connection = null;
    }
    this.paymentCompletedCallbacks.clear();
    this.paymentFailedCallbacks.clear();
    this.paymentCancelledCallbacks.clear();
    this.deviceStatusCallbacks = [];
    this.statusChangeCallbacks = [];
    this.localPrintHandlers.clear();
    this.pavoRefundCompletedCallbacks.clear();
    this.pavoRefundFailedCallbacks.clear();
    this.pavoCustomerQueryCompletedCallbacks.clear();
    this.pavoCustomerQueryFailedCallbacks.clear();
    this.tablePavoClosedCallbacks.clear();
    this.tableUpdatedCallbacks.clear();
    this.tableClosedCallbacks.clear();
    this._onlineDevices.clear();
    this._devicePresenceSubs.clear();
    this.quickSaleResultResolvedCallbacks.clear();
  }
}

export const pavoSignalRService = new PavoSignalRService();
export default pavoSignalRService;
